import React from 'react';
import Instanoti from 'instanoti-js';
import { toast } from 'react-toastify';
import isMobile from 'ismobilejs';
import Registry from 'registry';

import Notification from '../components/notification/ItemPopupNotification';

import {
    receiveInstantNotification,
    RECEIVE_INSTANT_NOTIFICATION
} from 'reducers/memberNotificationList';

import { receiveMemberVirtualGift } from 'reducers/memberVirtualGifts';
import { getMemberMessageList } from 'reducers/memberMessageList';
import { getMemberGuestbook } from 'reducers/memberGuestbook';
import { getWallMaterialAction } from 'reducers/wallMaterials';

import { getProfile } from '../backend-helpers/profile';
import { getWallMaterial } from '../backend-helpers/wallMaterial';
import { getMemberVirtualGifts } from '../backend-helpers/memberVirtualGifts';

import {
    LOGIN_SUCCESS,
    LOGOUT_SUCCESS,
    SET_CURRENT_USER,
    getCurrentUser,
    setCurrentUserCounters,
} from 'reducers/currentUser';

import { newMemberFollowed } from 'reducers/memberFollowed'

// The instanoti socket authenticates with the session cookie, like PM. instanoti-js
// reconnects every 30 s forever after any close (the server accepts the socket and
// drops it ~200 ms later when the session is gone), and has no close() of its own.
// So: connect only for a logged-in, activated user, keep one socket per user, close it
// (with its timers) on logout, and stop when the server keeps dropping it right away.

const QUICK_CLOSE_MS = 2000;      // closed this soon after opening = rejected
const QUICK_CLOSE_LIMIT = 3;      // stop after this many rejections in a row

function canUseInstanoti(user) {
    return !!user && typeof user.nick !== 'undefined' && user.active === 1 && !!user.id;
}

function stopInstanoti(instance) {
    if (!instance) return;
    // instanoti-js only schedules a reconnect when `reconnecting` is false
    instance.reconnecting = true;
    clearInterval(instance.reconnect_interval);
    clearInterval(instance.ping_interval);
    try {
        if (instance.socket) instance.socket.close();
    } catch (e) {}
}

const socketMiddleware = function socketMiddleware(store) {
    let socket;
    let socketUid = null;
    let toastId;
    return function (next) {
        return function (action) {
            const WSS_URL = Registry.get('WSS_URL');
            let wss_url = WSS_URL.match(/^wss:/) ? WSS_URL : Registry.get('PUBLIC_URL').replace(/^https?:/, "wss:") + WSS_URL;
            switch(action.type) {
                case LOGOUT_SUCCESS:
                    stopInstanoti(socket);
                    socket = undefined;
                    socketUid = null;
                    break;

                case LOGIN_SUCCESS:
                case SET_CURRENT_USER: {
                    const user = action.user;

                    if (!canUseInstanoti(user)) {
                        stopInstanoti(socket);
                        socket = undefined;
                        socketUid = null;
                        break;
                    }

                    // already connected for this user: don't open a second socket
                    if (socket && socketUid !== null && String(socketUid) === String(user.id)) break;

                    stopInstanoti(socket);
                    socketUid = user.id;

                    let openedAt = 0;
                    let quickCloses = 0;

                    socket = new Instanoti({
                        server_uri: wss_url,
                        on_open: data => {
                            openedAt = Date.now();
                            console.log("Websocket connected")
                        },
                        // note: instanoti-js never fires on_close (it emits "instanoti:close",
                        // the config callback is registered as "instanoti:on_close"), see below
                        on_close: data => {
                            console.log("Websocket disconnected")
                        },
                        on_error: err => {
                            console.log("Websocket error", err)
                        },
                        on_counter: data => {
                            store.dispatch(setCurrentUserCounters(data));
                        },
                        on_tip: data => {
                            store.dispatch(receiveInstantNotification(data));
                        },
                        on_new_follower: data => {
                            store.dispatch(receiveInstantNotification(data));
                            const { data: { meta: { user: { id, nick }}}} = data;
                            getProfile({id, nick})
                                .then(res => store.dispatch(newMemberFollowed(res.user)))
                                .catch(err => console.error(err.message));
                        },
                        on_gift: data => {
                            store.dispatch(receiveInstantNotification(data));
                            const { data: { entity_id }} = data;
                            getMemberVirtualGifts({ id: entity_id })
                                .then(res => store.dispatch(receiveMemberVirtualGift(res.data)))
                                .catch(err => console.error(err.message));
                        },
                        on_message: data => {
                            // check if conversation with this user opened
                            let id;
                            const state = store.getState();
                            if (state.router) {
                                const { router: { location: { pathname }}} = state;
                                id = pathname.split("/").pop();
                            }
                            if (+id !== data.data.meta.user.id) {
                                store.dispatch(receiveInstantNotification(data, true));
                                store.dispatch(getMemberMessageList({ conversation_id: data.data.meta.user.id}));
                            }
                        },
                        on_membership_gift: data => {
                            store.dispatch(receiveInstantNotification(data));
                            store.dispatch(getCurrentUser());
                        },
                        on_membership_gift_public: data => {
                            store.dispatch(receiveInstantNotification(data));
                        },
                        on_guestbook_message: data => {
                            store.dispatch(receiveInstantNotification(data));
                            store.dispatch(getMemberGuestbook())
                        },
                        on_wall: data => {
                            const { currentUser } = store.getState();
                            if ((currentUser.id !== data.data.meta.user.id)
                                && currentUser.notification_settings.wall_notify.site) {
                                    store.dispatch(receiveInstantNotification(data));
                            }
                            getWallMaterial(data.data.entity_id)
                                .then(res => store.dispatch(getWallMaterialAction(res.data)))
                                .catch(err => console.error(err.message));
                        },
                        on_followed_user_is_online: data => {
                            store.dispatch(receiveInstantNotification(data, true));
                        },
                        on_new_personal_room: data => {
                            store.dispatch(receiveInstantNotification(data, true));
                        },
                        on_new_showroom: data => {
                            store.dispatch(receiveInstantNotification(data, true));
                        },
                        on_user_membership_expired: data => {
                            store.dispatch(receiveInstantNotification(data));
                        },
                        on_user_membership_almost_expired: data => {
                            store.dispatch(receiveInstantNotification(data));
                        },
                        on_stream_record_error: data => {
                            store.dispatch(receiveInstantNotification(data));
                        },
                        reconnect: true
                    });

                    // Registered after instanoti-js's own reconnect handler, so it runs first
                    // (handlers fire in reverse order) and can cancel the reconnection.
                    socket.on('instanoti:close', data => {
                        const instance = data && data.instance;

                        if (openedAt && Date.now() - openedAt < QUICK_CLOSE_MS) {
                            quickCloses++;
                        } else {
                            quickCloses = 0;
                        }
                        openedAt = 0;

                        if (quickCloses >= QUICK_CLOSE_LIMIT && instance) {
                            console.log("Websocket rejected repeatedly, stopping reconnection");
                            stopInstanoti(instance);
                            if (socket === instance) {
                                socket = undefined;
                                socketUid = null;
                            }
                        }
                    });
                    break;
                }
            case RECEIVE_INSTANT_NOTIFICATION:
                if (!isMobile.any) {
                    if (!toastId) {
                        toastId = toast(
                            <Notification data={action.data} />,
                            { onClose: () => toastId = undefined }
                        );
                    } else {
                        toast.update(toastId, {
                            render: <Notification data={action.data} />
                        });
                    }
                }
            }
        return next(action);
      };
    };
  };

export default socketMiddleware;



// WEBPACK FOOTER //
// ./src/middleware/socketMiddleware.js